import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  IconButton,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import ImageIcon from '@mui/icons-material/Image';
import { useMemo, useState, useEffect } from 'react';
import type { ChapterDto } from '../../api/queries/books';
import { useParagraphs } from '../../api/queries/books';
import { useReorderChapter } from '../../api/queries/reorder';
import { BulkGenerationDialog } from './BulkGenerationDialog';
import { ChapterNarrationDialog } from './ChapterNarrationDialog';

interface Props {
  bookId: string;
  chapters: ChapterDto[];
  activeChapterId: string | null;
  onSelect: (id: string) => void;
}

/**
 * Sortable chapter outline using @dnd-kit/sortable. Reorder fires the backend
 * ReorderChapterCommand; TanStack Query invalidation refreshes the list. Arrow-key
 * navigation + Enter select are wired for accessibility (Phase 1 keybindings spec).
 */
export function ChapterOutline({
  bookId,
  chapters,
  activeChapterId,
  onSelect,
}: Props) {
  const reorder = useReorderChapter(bookId);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );
  const [order, setOrder] = useState<ChapterDto[]>(chapters);

  useEffect(() => {
    setOrder(chapters);
  }, [chapters]);

  const ids = useMemo(() => order.map((c) => c.id), [order]);

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(order, oldIndex, newIndex);
    setOrder(next);
    void reorder.mutateAsync({
      id: String(active.id),
      newSequenceOrder: newIndex,
    });
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!activeChapterId) return;
    const currentIdx = order.findIndex((c) => c.id === activeChapterId);
    if (event.key === 'ArrowDown' && currentIdx < order.length - 1) {
      event.preventDefault();
      onSelect(order[currentIdx + 1]!.id);
    } else if (event.key === 'ArrowUp' && currentIdx > 0) {
      event.preventDefault();
      onSelect(order[currentIdx - 1]!.id);
    }
  };

  return (
    <Stack spacing={1} onKeyDown={onKeyDown} tabIndex={0}>
      <Typography variant="subtitle2">Chapters</Typography>
      {order.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 2, textAlign: 'center' }}
        >
          This draft has no chapters yet. Use the toolbar to add one manually or
          generate from an outline.
        </Typography>
      ) : (
        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            {order.map((chapter) => (
              <ChapterRow
                key={chapter.id}
                chapter={chapter}
                bookId={bookId}
                selected={chapter.id === activeChapterId}
                onClick={() => onSelect(chapter.id)}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
    </Stack>
  );
}

function ChapterRow({
  chapter,
  bookId,
  selected,
  onClick,
}: {
  chapter: ChapterDto;
  bookId: string;
  selected: boolean;
  onClick: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: chapter.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [bulkOpen, setBulkOpen] = useState(false);
  const [narrationOpen, setNarrationOpen] = useState(false);
  const paragraphsQuery = useParagraphs(
    bulkOpen || narrationOpen ? chapter.id : undefined,
  );

  return (
    <Box ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Stack direction="row" alignItems="center">
        <ListItemButton selected={selected} onClick={onClick} sx={{ flex: 1 }}>
          <ListItemText
            primary={chapter.title}
            secondary={`${chapter.paragraphCount} paragraph${chapter.paragraphCount === 1 ? '' : 's'}`}
          />
        </ListItemButton>
        <Tooltip title="Narrate chapter">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setNarrationOpen(true);
            }}
            sx={{ mr: 0.5 }}
          >
            <HeadphonesIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Generate images for chapter">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setBulkOpen(true);
            }}
            sx={{ mr: 0.5 }}
          >
            <ImageIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      {bulkOpen && (
        <BulkGenerationDialog
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          bookId={bookId}
          chapterId={chapter.id}
          paragraphs={paragraphsQuery.data ?? []}
          mode="chapter"
        />
      )}
      {narrationOpen && (
        <ChapterNarrationDialog
          open={narrationOpen}
          onClose={() => setNarrationOpen(false)}
          bookId={bookId}
          chapterId={chapter.id}
          paragraphs={paragraphsQuery.data ?? []}
        />
      )}
    </Box>
  );
}
